import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, UrlService, } from "@tylertech/core-module";
import { ICustomViewComponent, } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { AppLinkParms, FilterItem, IChartCriteria, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "general-ledger-cash-balance-by-fund-chart",
    templateUrl: "./cashBalanceByFundChart.tmpl.html",
    styleUrls: ["./cashBalanceByFundChart.component.scss"],
    standalone: false
})
export class CashBalanceByFundChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $intentService = inject(IntentService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    readonly ChartTitle: string = "Cash Balance By Fund";
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;
    isLoaded: boolean;
    ChartSubtitle: string;
    total: string;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }

                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    updateChartConfiguration(): void
    {
        this.ChartSubtitle = this.chartCriteria.ChartDatas[0].Additional[0];
        this.chartConfig = {
            type: "bar",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        position: "left",
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                let label = tooltipItem.label;
                                if (this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterItem)
                                {
                                    label = `${this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].FilterItem.Value}`;
                                }
                                const value = this.chartCriteria.ChartDatas[0].Data[tooltipItem.dataIndex].Value;
                                return `${label} ( Total: ${formatCurrency(value, this.locale, "$")} )`;
                            },
                        },
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return;
                    }
                    const index = element.index;
                    const filter: FilterItem[] = [];
                    if (this.chartCriteria.ChartDatas[0].Data[index].FilterItem)
                    {
                        filter.push(this.chartCriteria.ChartDatas[0].Data[index].FilterItem);
                    }
                    if (this.chartCriteria.ChartDatas[0].Additional && this.chartCriteria.ChartDatas[0].Additional[1])
                    {
                        this.chartCriteria.ChartDatas[0].Additional[1].forEach(element =>
                        {
                            filter.push(element);
                        });
                    }
                    const parms: AppLinkParms = {
                        App: "glaccounts",
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms)
                        .then(x => x.Result)
                        .then((result: string) =>
                        {
                            const index = result.indexOf("?");
                            if (result.includes("apps/form"))
                            {
                                this.$navigationService.navigateToApp(result);
                            }
                            else if (index > 0)
                            {
                                this.$intentService.resolve("GLAccounts", "Search", {
                                    querystring: result.substring(index, result.length),
                                });
                            }
                        });
                },
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: this.chartCriteria.ChartDatas[0].Labels,
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: {
                datasets: [
                    {
                        type: "bar",
                        data: this.chartCriteria.ChartDatas[0].Data.map(x => x.Value),
                        backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length),
                        categoryPercentage: 0.5,
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
